import React, { FunctionComponent, useState, ReactNode, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { CardBrand } from '@afterpay/types'
import { InputCluster, InputClusterProps, InputKinds, InputOnChange, PaymentIcons, Text } from '@afterpaytouch/core'
import { useVisible } from '../../utils/hooks'
import {
  AmexCreditCardFormats,
  AmexNumberFormat,
  CardFieldSchema,
  CardFormErrors,
  CardFormField,
  StandardCreditCardFormats,
  StandardNumberFormat,
} from '../../types'
import { isValidObject } from '../../utils/object'
import { CardNumberValidity, CardExpiryValidity } from './types'
import { cardExpiryValidator, cardNumberValidator, getCardBrandFromCardNumber, getCardIconFromCardBrand, validateCardHolderName, validateCVV } from './utils'

import './style.css' // TODO: do we need this?
import { isNullOrUndefined } from '@afterpay/utils'

export const CardField: FunctionComponent<CardFieldSchema> = ({
  show = true,
  testNameSpace = '',
  showCardHolderNameField,
  name = '',
  fieldTitle,
  fieldTitleSize,
  defaultValues,
  acceptableCardBrands,
  lastPaymentDate,
  errorMessages,
  blockedBins = [],
  labels,
  showHeading = true,
  validationMode = 'onBlur',
  paymentType,
  showCardIcon = false,
  customizedHeading,
}) => {
  const {
    formState: { touchedFields },
    register,
    setValue,
    trigger,
  } = useFormContext()
  const { isVisible, values } = useVisible({ name, show })
  const [selectedCardBrand, setSelectedCardBrand] = useState<CardBrand | undefined>(undefined)
  const [cardErrors, setCardErrors] = useState<CardFormErrors>({})
  const [creditCardFormat, setCreditCardFormat] = useState<AmexCreditCardFormats | StandardCreditCardFormats>(
    getCardBrandFromCardNumber(defaultValues.cardNumber) === CardBrand.AMEX ? { ...AmexNumberFormat } : { ...StandardNumberFormat }
  )
  const cardExpiryLimitInMonths = 2
  const validateCardNumber = cardNumberValidator(acceptableCardBrands, blockedBins)
  const validateCardExpiry = cardExpiryValidator(lastPaymentDate, cardExpiryLimitInMonths, paymentType)
  const isValidationTriggerOnChange = validationMode === 'onChange'

  register(name, {
    value: values ?? defaultValues,
    validate: (values) => {
      let valid = true

      if (Object.keys(cardErrors).length > 0) {
        return false
      }

      Object.values(values).forEach((value) => {
        if (value === '' || value === undefined || value === null) {
          valid = false
        }
      })

      return valid
    },
  })

  useEffect(() => {
    // Validate when card error changes so error is affecting isValid and react hook form
    trigger(name)
  }, [cardErrors, name])

  const renderErrors = (): ReactNode => {
    const { cardNumber, cvv, expiryDate, cardHolderName } = cardErrors ?? {}

    const errors = (): JSX.Element => {
      return (
        <>
          <Text size={errorMessages?.textSize}>{cardHolderName}</Text>
          <Text size={errorMessages?.textSize}>{cvv}</Text>
          <Text size={errorMessages?.textSize}>{expiryDate}</Text>
          <Text size={errorMessages?.textSize}>{cardNumber}</Text>
        </>
      )
    }

    return isValidObject(cardErrors) ? (
      <div className='formErrors' data-testid='form-errors'>
        {errors()}
      </div>
    ) : null
  }

  const handleFieldChange =
    (mappedInputName: string) =>
    (ev: InputOnChange, rawValue: string): void => {
      setValue(`${name}.${mappedInputName}`, rawValue, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: true,
      })
      if (isValidationTriggerOnChange) {
        formValidate()
      }
    }

  const handleExpiryChange =
    (mappedInputName: string) =>
    (ev: InputOnChange, _: string, viewValue: string): void => {
      setValue(`${name}.${mappedInputName}`, viewValue, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: true,
      })
      if (isValidationTriggerOnChange) {
        formValidate()
      }
    }

  const handleCardNumberChange =
    (mappedInputName: string) =>
    (ev: InputOnChange, rawValue: string): void => {
      setValue(`${name}.${mappedInputName}`, rawValue, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: true,
      })
      const cardBrand = getCardBrandFromCardNumber(rawValue)
      setSelectedCardBrand(cardBrand)
      cardBrand === CardBrand.AMEX ? setCreditCardFormat({ ...AmexNumberFormat }) : setCreditCardFormat({ ...StandardNumberFormat })
      if (isValidationTriggerOnChange) {
        formValidate()
      }
    }

  const formValidate = (): void => {
    const newCardErrors: CardFormErrors = {}
    const cardNumber = values?.[CardFormField.cardNumber]
    const validCVV = validateCVV(values?.[CardFormField.cvv], selectedCardBrand)
    const validCardNumber = validateCardNumber(cardNumber)
    const validExpiry = validateCardExpiry(values?.[CardFormField.expiryDate])
    const validCardHolderName = validateCardHolderName(values?.[CardFormField.cardHolderName])
    const brandNotAcceptedError = errorMessages?.cardNumber?.brandNotAccepted
    const binNotAcceptedError = errorMessages?.cardNumber?.binNotAccepted

    if (touchedFields?.[name]?.[CardFormField.cardHolderName] && !validCardHolderName) {
      newCardErrors[CardFormField.cardHolderName] = errorMessages?.cardHolderName?.empty
    }

    if (touchedFields?.[name]?.[CardFormField.cvv] && !validCVV) {
      newCardErrors[CardFormField.cvv] = errorMessages?.cvv?.invalid
    }

    if (touchedFields?.[name]?.[CardFormField.cardNumber] && validCardNumber !== CardNumberValidity.Valid) {
      switch (validCardNumber) {
        case CardNumberValidity.InvalidNumber:
          newCardErrors[CardFormField.cardNumber] = errorMessages?.cardNumber?.invalid
          break
        case CardNumberValidity.CardBrandIneligible:
          newCardErrors[CardFormField.cardNumber] =
            typeof brandNotAcceptedError === 'string' ? brandNotAcceptedError : brandNotAcceptedError(selectedCardBrand as CardBrand)
          break
        case CardNumberValidity.BinIneligible:
          if (typeof binNotAcceptedError !== 'undefined') {
            newCardErrors[CardFormField.cardNumber] = typeof binNotAcceptedError === 'string' ? binNotAcceptedError : binNotAcceptedError(cardNumber)
          }

          break
      }
    }

    if (touchedFields?.[name]?.[CardFormField.expiryDate] && validExpiry !== CardExpiryValidity.Valid) {
      switch (validExpiry) {
        case CardExpiryValidity.Invalid:
          newCardErrors[CardFormField.expiryDate] = errorMessages?.expiryDate?.empty
          break
        case CardExpiryValidity.Expired:
          newCardErrors[CardFormField.expiryDate] = errorMessages?.expiryDate?.past
          break
        case CardExpiryValidity.ExpiringSoon:
          newCardErrors[CardFormField.expiryDate] = errorMessages?.expiryDate?.expireSoon
          break
        case CardExpiryValidity.ExpireInLimitMonths:
          newCardErrors[CardFormField.expiryDate] = errorMessages?.expiryDate?.expireInLimitMonths
          break
      }
    }

    setCardErrors(newCardErrors)
  }

  const inputClusterProps = (): InputClusterProps => {
    const { cardHolderName, expiryDate, cardNumber, cvv } = CardFormField

    const cardNumberSchema = {
      name: cardNumber,
      label: labels[cardNumber] ?? '',
      format: creditCardFormat.NUMBER_FORMAT,
      autoComplete: 'cc-number',
      kind: 'tel' as InputKinds,
      error: Boolean(cardErrors?.[cardNumber]),
      icon: showCardIcon ? getCardIconFromCardBrand(acceptableCardBrands, selectedCardBrand) : undefined,
      showErrorIconWithCustomIcon: showCardIcon,
      value: defaultValues?.[cardNumber] ?? '',
      testNameSpace: `${testNameSpace}-cardNumber`,
      onBlur: formValidate,
      onChange: handleCardNumberChange(cardNumber),
    }

    return {
      Top: showCardHolderNameField
        ? {
            name: cardHolderName,
            label: labels[cardHolderName] ?? '',
            error: Boolean(cardErrors?.[cardHolderName]),
            value: defaultValues?.[cardHolderName] ?? '',
            testNameSpace: `${testNameSpace}-cardHolderName`,
            autoComplete: 'cc-name',
            onBlur: formValidate,
            onChange: handleFieldChange(cardHolderName),
          }
        : cardNumberSchema,
      Middle: showCardHolderNameField ? cardNumberSchema : undefined,
      Left: {
        name: expiryDate,
        label: labels[expiryDate] ?? '',
        format: '00/00',
        error: Boolean(cardErrors?.[expiryDate]),
        value: defaultValues[expiryDate] ?? '',
        testNameSpace: `${testNameSpace}-cardExpiry`,
        autoComplete: 'cc-exp',
        kind: 'tel',
        onBlur: formValidate,
        onChange: handleExpiryChange(expiryDate),
      },
      Right: {
        name: cvv,
        label: labels[cvv] ?? '',
        format: creditCardFormat.CVV_FORMAT,
        error: Boolean(cardErrors?.[cvv]),
        value: defaultValues?.[cvv] ?? '',
        testNameSpace: `${testNameSpace}-cardCvv`,
        autoComplete: 'cc-csc',
        kind: 'tel',
        onBlur: formValidate,
        onChange: handleFieldChange(cvv),
      },
      onChange: () => {},
      testNameSpace,
    }
  }

  // TODO: consider remove heading from FormBuilder
  // TODO: integrate better into react-hook-form context

  return isVisible ? (
    <div className='containerColumn' {...{ 'data-testid': `${testNameSpace}-wrapper` }}>
      {showHeading &&
        (!isNullOrUndefined(customizedHeading) ? (
          <div className='heading'>{customizedHeading}</div>
        ) : (
          <div className='heading'>
            <Text renderAs='span' size={fieldTitleSize} bold testNameSpace={`${testNameSpace}-heading`}>
              {fieldTitle}
            </Text>
            <PaymentIcons allowedCardBrand={acceptableCardBrands} selectedCardBrand={selectedCardBrand} />
          </div>
        ))}
      <div className='clusterContainer'>
        <InputCluster {...inputClusterProps()} />
      </div>
      {renderErrors()}
    </div>
  ) : null
}
